import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Users, UserPlus, Loader2, Lock, Trash2, Edit2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../config';

const SpaceAccessManager = ({ spaceId, spaceName, orgId }) => {
  const { currentOrg } = useAuth();
  const [accessList, setAccessList] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAccess, setShowAddAccess] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [canView, setCanView] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canManage = currentOrg?.user_role === 'company';

  const fetchAccessList = useCallback(async () => {
    if (!spaceId || !orgId) return;
    
    try {
      const response = await axios.get(`${API_URL}/organizations/${orgId}/spaces/${spaceId}/access`);
      setAccessList(response.data);
    } catch (error) {
      console.error('Failed to fetch access list:', error);
    }
  }, [spaceId, orgId]);

  const fetchMembers = useCallback(async () => {
    if (!orgId) return;
    
    try {
      const response = await axios.get(`${API_URL}/organizations/${orgId}/members`);
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  }, [orgId]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchAccessList(), fetchMembers()]);
    setLoading(false);
  }, [fetchAccessList, fetchMembers]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/organizations/${orgId}/spaces/${spaceId}/access`, {
        user_id: selectedUser,
        can_view: canView,
        can_edit: canEdit,
      });
      setAccessList(prev => [...prev, response.data]);
      setShowAddAccess(false);
      setSelectedUser('');
      setCanView(true);
      setCanEdit(false);
      toast.success('Access granted');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to grant access';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevokeAccess = async (accessId) => {
    if (!window.confirm('Remove this user\'s access to the space?')) return;

    try {
      await axios.delete(`${API_URL}/organizations/${orgId}/spaces/${spaceId}/access/${accessId}`);
      setAccessList(prev => prev.filter(a => a.id !== accessId));
      toast.success('Access revoked');
    } catch (error) {
      toast.error('Failed to revoke access');
    }
  };

  const handleUpdateAccess = async (accessId, updates) => {
    try {
      const access = accessList.find(a => a.id === accessId);
      await axios.put(`${API_URL}/organizations/${orgId}/spaces/${spaceId}/access/${accessId}`, {
        user_id: access.user_id,
        can_view: updates.can_view ?? access.can_view,
        can_edit: updates.can_edit ?? access.can_edit,
      });
      setAccessList(prev => prev.map(a => 
        a.id === accessId ? { ...a, ...updates } : a
      ));
      toast.success('Access updated');
    } catch (error) {
      toast.error('Failed to update access');
    }
  };

  // Members not yet having access
  const availableMembers = members.filter(
    m => !accessList.some(a => a.user_id === m.user_id)
  );

  if (loading) {
    return (
      <div className="py-8 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
      </div>
    );
  }

  return (
    <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="space-access-manager">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-[Manrope] text-slate-900 dark:text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-slate-500" />
              {spaceName} Access Control
            </CardTitle>
            <CardDescription>Manage who can view or edit this space</CardDescription>
          </div>
          {canManage && (
            <Button
              onClick={() => setShowAddAccess(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={availableMembers.length === 0}
              data-testid="grant-access-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Grant Access
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {accessList.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>View</TableHead>
                <TableHead>Edit</TableHead>
                {canManage && <TableHead className="w-10"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {accessList.map((access) => (
                <TableRow key={access.id} data-testid={`access-row-${access.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <span className="text-sm font-medium text-white">
                          {access.user_name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-slate-900 dark:text-white">
                          {access.user_name}
                        </div>
                        <div className="text-sm text-slate-500">{access.user_email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {canManage ? (
                      <Switch
                        checked={access.can_view}
                        onCheckedChange={(checked) => handleUpdateAccess(access.id, { can_view: checked })}
                      />
                    ) : (
                      <Badge className={access.can_view ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}>
                        {access.can_view ? 'Yes' : 'No'}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {canManage ? (
                      <Switch
                        checked={access.can_edit}
                        onCheckedChange={(checked) => handleUpdateAccess(access.id, { can_edit: checked })}
                      />
                    ) : (
                      <Badge className={access.can_edit ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}>
                        {access.can_edit ? 'Yes' : 'No'}
                      </Badge>
                    )}
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRevokeAccess(access.id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`revoke-access-${access.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="py-8 text-center">
            <Shield className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400 text-sm">No specific access rules</p>
            <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">
              This space uses default visibility settings
            </p>
          </div>
        )}
      </CardContent>

      {/* Grant Access Dialog */}
      <Dialog open={showAddAccess} onOpenChange={setShowAddAccess}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Grant Space Access</DialogTitle>
            <DialogDescription>Give a team member access to "{spaceName}"</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleGrantAccess}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="select-user">Select Member</Label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger id="select-user" data-testid="select-user-trigger">
                    <SelectValue placeholder="Choose a member" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMembers.map(member => (
                      <SelectItem key={member.user_id} value={member.user_id}>
                        {member.user_name} ({member.user_email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Can View</Label>
                    <p className="text-xs text-slate-500">User can see this space</p>
                  </div>
                  <Switch checked={canView} onCheckedChange={setCanView} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Can Edit</Label>
                    <p className="text-xs text-slate-500">User can edit documents</p>
                  </div>
                  <Switch checked={canEdit} onCheckedChange={setCanEdit} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddAccess(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={submitting || !selectedUser}
                data-testid="confirm-grant-access"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Granting...
                  </>
                ) : (
                  'Grant Access'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SpaceAccessManager;
